import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classNames from "classnames";

// Actions
import {
  createNewStudy,
  setVirtualKeyboard,
  toggleExistingParticipantsDialog,
  fetchEditExistingParticipantInfo,
  setView,
  setTemplatePrompt,
  fetchLocalStudiesList,
  fetchRemoteParticipantLog,
  fetchCheckWifi,
} from "../../actions";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Card from "@material-ui/core/Card";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import FormLabel from "@material-ui/core/FormLabel";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Fab from "@material-ui/core/Fab";
import Tooltip from "@material-ui/core/Tooltip";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

// Icons
import CircularProgress from "@material-ui/core/CircularProgress";
import CloudDoneIcon from "@material-ui/icons/CloudDone";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
// Other
import WindowedSelect, { components } from "react-windowed-select";
import { wifiEnabled, birthYearWheel } from "../../constants/constants";
import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import { viewIds } from "../../constants/componentMap";
import Header from "../Header/Header";
import ExistingParticipantDialog from "./ExistingParticipantDialog.js";
import ParticipantWarningDialog from "./ParticipantWarningDialog.js";
import SnackbarInfo from "../Shared/Snackbar";
import VirtualKeyboard from "../Shared/VirtualKeyboard";
import { CloudSync } from "../../constants/customIcons";
const useStyles = makeStyles({
  fill: {
    flex: "1 0 auto",
  },
  cardRoot: (props) => ({
    ...customTheme(props.theme).card.root,
    maxWidth: "600px",
    padding: "15px 35px 20px 35px",
  }),
  title: {
    ...customTheme().card.title,
    paddingBottom: "5px",
  },
  protocolFormItem: {
    width: "75%",
  },
  formGroupItem: {
    ...customTheme().card.contentItem,
    width: "96%",
    margin: "20px 10px 0px 10px",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  formTextItem: {
    ...customTheme().card.contentItem,
    width: "96%",
    margin: "20px 10px 0px 10px",
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  formItemLabel: { marginRight: "5px", width: "25%" },
  textFieldFormItem: {
    width: "75%",
    "& .MuiFormLabel-root.Mui-error": {
      color: colors.error,
    },
    "& .MuiInput-underline.Mui-error:after": {
      borderBottomColor: colors.error,
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "white",
    },
    // "& .MuiInputBase-input": {
    //   paddingBottom: "0px",
    // },
  },
  buttonContainer: {
    ...customTheme().card.buttonContainer,
    paddingTop: "0px",
  },
  select: {
    marginRight: "0px",
  },
  button: (props) => customTheme(props.theme).buttonPrimary,
  icon: customTheme().icon,
  copyright: customTheme().copyright,
  menuScroll: (props) => ({
    "& .MuiMenu-paper": {
      ...customTheme(props.theme).customScrollBar,
    },
    "& .MuiListItem-root": {
      "&:hover": {
        backgroundColor: colors[props.theme].menuHoverColor,
      },
    },
  }),
  skinTypeFormButtonGroup: {
    width: "75%",
  },
  skinTypeFormButton: {
    textTransform: "none",
    minWidth: "17%",
    padding: "0px",
  },
  genderFormButtonGroup: {
    display: "flex",
    width: "75%",
    justifyContent: "space-between",
  },
  genderFormButton: {
    textTransform: "none",
    minWidth: "55px",
    padding: "0px 2px 0px 2px",
  },
  tabContainer: {
    display: "flex",
    width: "100%",
    marginTop: "10px",
  },
  tabSelected: {
    width: "50%",
    textTransform: "none",
    marginBottom: "0px",
    height: "30px",
    padding: "0px",
    alignSelf: "flex-end",
    borderRadius: "10px 10px 0px 0px",
    marginLeft: "1px",
    backgroundColor: (props) => colors[props.theme].dialogBkg,
    boxShadow: colors.dialogBoxShadow,
  },
  tabUnselected: {
    width: "50%",
    textTransform: "none",
    marginBottom: "2px",
    height: "28px",
    padding: "0px",
    alignSelf: "flex-end",
    borderRadius: "10px 10px 0px 0px",
    marginLeft: "1px",
    backgroundColor: colors.dialogTabUnselected,
    "&:hover": {
      backgroundColor: colors.dialogTabUnselected,
    },
    boxShadow: (props) =>
      props.theme === "dark"
        ? "inset rgba(31,31,31, 0.5) 0px 3px 6px 2px"
        : "inset rgba(31,31,31, 0.1) 0px 3px 6px 2px",
  },
  testModeFormControl: {
    ...customTheme().card.contentItem,
    margin: "0px 10px 0px 10px",
    pointerEvents: "none",
  },
  checkbox: {
    pointerEvents: "auto",
    padding: "9px 9px 9px 0px",
  },
  fabProgress: {
    top: "-5px",
    left: "-5px",
    zIndex: 1,
    position: "absolute",
  },
  cloudButtonWrap: {
    position: "relative",
  },
  cloudButton: (props) => ({
    padding: "0px",
    width: "36px",
    height: "36px",
    backgroundColor: props.remoteParticipantLogRecevied
      ? colors[props.theme].primary
      : colors.secondaryText,

    "&:hover": {
      backgroundColor: props.remoteParticipantLogRecevied
        ? colors[props.theme].primaryLight
        : "#e0e0e0",
    },
  }),
  subDiv: {
    ...customTheme().card.contentItem,
    flexDirection: "row",
    display: "flex",
    width: "90%",
    justifyContent: "space-between",
    alignItems: "baseline",
    margin: "0px 10px 15px 10px",
  },
  testModeDiv: {
    height: "42px",
    marginTop: "10px",
  },
  appBar: {
    backgroundColor: (props) => colors[props.theme].headerBkg,
    "& .MuiTabs-root": {
      height: "40px",
    },
  },
  input: {
    "& .MuiInputBase-input": {
      paddingBottom: "0px",
    },
    paddingBottom: "0px",
  },
});

function NewStudy(props) {
  const classes = useStyles(props);
  const [localState, setLocalState] = React.useState({
    testMode: false,
    tab: "NEW_PARTICIPANT",
    participantIdSpecialCharacterCheck: false,
    warningDialog: {
      status: false,
      prompt: null,
    },
    selectedExistingParticipantId: null,
  });

  const [keyboardInput, setKeyboardInput] = React.useState({
    inputName: null,
    inputUpdate: {
      participantId: "",
      otherId: "",
    },
  });
  const [studyInfo, setStudyInfo] = React.useState({
    participantId: "",
    birthYear: "",
    skinType: "",
    protocolId: "",
    protocolName: "",
    gender: "",
    otherId: "",
  });
  useEffect(() => {
    // There is only one protocol avaialble, automatically pick that protocol
    if (Object.keys(props.protocols).length === 1) {
      setStudyInfo({
        ...studyInfo,
        protocolId: Object.keys(props.protocols)[0],
        protocolName:
          props.protocols[Object.keys(props.protocols)[0]].protocolName,
      });
    }
  }, [props.protocols]);

  useEffect(() => {
    // Increment the participant ID for the selected protocol
    if (
      props.localParticipantLogRecevied &&
      studyInfo.protocolId !== "" &&
      !localState.testMode &&
      localState.tab === "NEW_PARTICIPANT"
    ) {
      console.log("RECALCULATE P. ID");
      let participantsNumber = [];
      let maxParticipantsNumber;
      if (
        Object.keys(props.logParticipantsByProtocolId).includes(
          studyInfo.protocolId
        )
      ) {
        for (let filteredParticipantId of props.logParticipantsByProtocolId[
          studyInfo.protocolId
        ]) {
          let participantIdSplit = filteredParticipantId.split("_");
          let participantIdNum = Number(
            participantIdSplit[participantIdSplit.length - 1]
          );
          if (!isNaN(participantIdNum)) {
            participantsNumber.push(participantIdNum);
          }
        }
        maxParticipantsNumber = Math.max(...participantsNumber) + 1;
        maxParticipantsNumber = maxParticipantsNumber.toString();
      } else {
        maxParticipantsNumber = "-Infinity";
      }

      if (maxParticipantsNumber === "-Infinity") {
        maxParticipantsNumber = "1";
      }
      while (maxParticipantsNumber.length < 3)
        maxParticipantsNumber = "0" + maxParticipantsNumber;
      setKeyboardInput({
        ...keyboardInput,
        inputUpdate: {
          ...keyboardInput.inputUpdate,
          participantId: maxParticipantsNumber,
        },
      });
      setStudyInfo({ ...studyInfo, participantId: maxParticipantsNumber });
    }
  }, [
    studyInfo.protocolId,
    localState.testMode,
    localState.tab,
    props.remoteParticipantLogRecevied,
  ]);

  const handleFormChange = (input_value) => (event) => {
    switch (input_value.field) {
      case "protocolId":
        setStudyInfo({
          ...studyInfo,
          [input_value.field]: event.target.value,
          protocolName: props.protocols[event.target.value].protocolName,
          birthYear: "",
          skinType: "",
          gender: "",
          otherId: "",
        });
        setKeyboardInput({
          ...keyboardInput,
          inputUpdate: {
            ...keyboardInput.inputUpdate,
            otherId: "",
          },
        });

        setLocalState({ ...localState, tab: "NEW_PARTICIPANT" });
        break;
      case "participantId":
        let newparticipantName = event.target ? event.target.value : event;
        // Check for special characters. Special characters are not allowed in participant ID
        if (
          RegExp(/[~`!#$%^&*+=\-[\]\\';,/{}|\\":<>?]/).test(newparticipantName)
        ) {
          setLocalState({
            ...localState,
            participantIdSpecialCharacterCheck: true,
          });
        } else {
          setLocalState({
            ...localState,
            participantIdSpecialCharacterCheck: false,
          });
        }
        if (event.target) {
          // Update virtualKeyboard with the manually entered studyInfo
          setKeyboardInput({
            ...keyboardInput,
            inputUpdate: {
              ...keyboardInput.inputUpdate,
              [input_value.field]: event.target.value,
            },
          });
        }
        setStudyInfo({ ...studyInfo, [input_value.field]: newparticipantName });
        break;
      case "skinType":
        setStudyInfo({
          ...studyInfo,
          [input_value.field]: input_value.type,
        });
        break;

      case "gender":
        setStudyInfo({
          ...studyInfo,
          [input_value.field]: input_value.type,
        });
        break;

      default:
        if (event.target) {
          setStudyInfo({
            ...studyInfo,
            [input_value.field]: event.target.value,
          });
          // Update virtualKeyboard with the manually entered studyInfo
          setKeyboardInput({
            ...keyboardInput,
            inputUpdate: {
              ...keyboardInput.inputUpdate,
              [input_value.field]: event.target.value,
            },
          });
        } else {
          setStudyInfo({ ...studyInfo, [input_value.field]: event });
        }
        break;
    }
  };

  const handleResetParticipantInfo = () => {
    setStudyInfo({
      ...studyInfo,
      participantId: "",
      birthYear: "",
      skinType: "",
      gender: "",
      otherId: "",
    });
    setKeyboardInput({
      ...keyboardInput,
      inputUpdate: {
        ...keyboardInput.inputUpdate,
        participantId: "",
        otherId: "",
      },
    });
  };
  const handleTestMode = (e) => {
    props.setVirtualKeyboard(false);
    if (e.target.checked) {
      setStudyInfo({
        ...studyInfo,
        participantId: "NO_ID",
        birthYear: "",
        skinType: "",
        gender: "",
        otherId: "",
      });
      setKeyboardInput({
        ...keyboardInput,
        inputUpdate: {
          ...keyboardInput.inputUpdate,
          participantId: "NO_ID",
          otherId: "",
        },
      });
    } else {
      handleResetParticipantInfo();
    }
    setLocalState({ ...localState, testMode: !localState.testMode });
  };

  const handleChangeTab = (clickedTab) => {
    if (clickedTab === "NEW_PARTICIPANT") {
      handleResetParticipantInfo();
    } else {
      props.toggleExistingParticipantsDialog();
    }
    setLocalState({ ...localState, tab: clickedTab });
  };

  const handleUseExistingParticipantInfo = (existingParticipantId) => {
    let existingParticipantInfo =
      props.logParticipantsById[existingParticipantId].participantInfo;

    setStudyInfo({
      ...studyInfo,
      participantId: existingParticipantInfo.participantId,
      birthYear: existingParticipantInfo.birthYear
        ? existingParticipantInfo.birthYear
        : "",
      skinType: existingParticipantInfo.skinType
        ? existingParticipantInfo.skinType
        : "",
      gender: existingParticipantInfo.gender
        ? existingParticipantInfo.gender
        : "",
      otherId: existingParticipantInfo.otherId
        ? existingParticipantInfo.otherId
        : "",
    });
    setKeyboardInput({
      ...keyboardInput,
      inputUpdate: {
        ...keyboardInput.inputUpdate,
        participantId: existingParticipantInfo.participantId,
        otherId: existingParticipantInfo.otherId,
      },
    });
    setLocalState({
      ...localState,
      selectedExistingParticipantId: existingParticipantId,
    });
  };
  const handleClickExistingParticipant = (existingParticipantId) => {
    if (
      props.logParticipantsById[existingParticipantId].modifiedProtocol.status
    ) {
      let allModifiedProtocols = [];
      for (let modifiedStudy of props.logParticipantsById[existingParticipantId]
        .modifiedProtocol.modifiedStudiesList) {
        allModifiedProtocols.push(modifiedStudy.protocolId);
      }
      if (allModifiedProtocols.includes(studyInfo.protocolId)) {
        setLocalState({
          ...localState,
          selectedExistingParticipantId: existingParticipantId,
          warningDialog: { status: true, prompt: "MODIFIED_PROTOCOL" },
        });
      } else {
        handleUseExistingParticipantInfo(existingParticipantId);
      }
    } else {
      handleUseExistingParticipantInfo(existingParticipantId);
    }
  };
  const handleParticipantWarningDialog = (propmt, updateRemote) => {
    switch (propmt) {
      case "UPDATE_INFO":
        // Proceed with the study
        handleSubmit();
        props.fetchEditExistingParticipantInfo(studyInfo, updateRemote);
        break;
      case "DISCARD_CHANGES":
        handleUseExistingParticipantInfo(
          localState.selectedExistingParticipantId
        );
        setLocalState({
          ...localState,
          warningDialog: { status: false, prompt: null },
        });
        break;
      case "USE_EXISTING":
        handleUseExistingParticipantInfo(
          studyInfo.protocolId + "_" + studyInfo.participantId
        );
        setLocalState({
          ...localState,
          tab: "EXISTING_PARTICIPANT",
          selectedExistingParticipantId:
            studyInfo.protocolId + "_" + studyInfo.participantId,
          warningDialog: { status: false, prompt: null },
        });
        break;

      case "CREATE_NEW":
        setLocalState({
          ...localState,
          warningDialog: { status: false, prompt: null },
        });
        break;

      case "FOLLOW_UP":
        props.fetchLocalStudiesList();
        props.setTemplatePrompt(true);
        props.setView(viewIds.loadStudy);

        break;
      case "CONTINUE_NEW":
        handleUseExistingParticipantInfo(
          localState.selectedExistingParticipantId
        );
        setLocalState({
          ...localState,
          warningDialog: { status: false, prompt: null },
        });
        break;
      default:
        //CONTINUE_NEW
        setLocalState({
          ...localState,
          warningDialog: { status: false, prompt: null },
        });
        break;
    }
  };

  const handleSubmitRequest = () => {
    if (!localState.testMode && localState.tab === "NEW_PARTICIPANT") {
      let enteredParticipantId =
        studyInfo.protocolId + "_" + studyInfo.participantId;
      if (props.logParticipantAllIds.includes(enteredParticipantId)) {
        setLocalState({
          ...localState,
          warningDialog: { status: true, prompt: "EXISTING_ID" },
        });
      } else {
        handleSubmit();
      }
    } else if (localState.tab === "EXISTING_PARTICIPANT") {
      let existingParticipantInfo =
        props.logParticipantsById[localState.selectedExistingParticipantId]
          .participantInfo;

      if (
        !(
          existingParticipantInfo.birthYear === studyInfo.birthYear &&
          existingParticipantInfo.skinType === studyInfo.skinType &&
          existingParticipantInfo.gender === studyInfo.gender &&
          existingParticipantInfo.otherId === studyInfo.otherId
        )
      ) {
        setLocalState({
          ...localState,
          warningDialog: { status: true, prompt: "INFO_EDITED" },
        });
      } else {
        handleSubmit();
      }
    } else {
      handleSubmit();
    }
  };
  const handleSubmit = () => {
    let newStudyInfo = {};
    if (!localState.testMode && localState.tab === "NEW_PARTICIPANT") {
      newStudyInfo = {
        ...studyInfo,
        participantId: studyInfo.protocolId + "_" + studyInfo.participantId,
      };
    } else {
      newStudyInfo = { ...studyInfo };
    }
    props.createNewStudy(newStudyInfo); //add newStudyInfo to reducer, fetch protocol from back-end, change the view
    props.setVirtualKeyboard(false);
  };
  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <ArrowDropDownIcon />
      </components.DropdownIndicator>
    );
  };

  return (
    <Grid
      item
      container
      className={classNames(classes.fill, "noselect")}
      direction="column"
    >
      {/* HEADER */}
      <Header
      //  eventSource={props.eventSource}
      />

      <Grid
        item
        container
        className={classes.fill}
        direction="column"
        justify="space-between"
      >
        <Grid
          item
          container
          className={classes.fill}
          direction="column"
          justify="center"
          alignItems="center"
        >
          <Grid item>
            {/* INFORMATION CARD */}
            <Card className={classes.cardRoot}>
              <Typography
                variant="h5"
                color="textPrimary"
                align="center"
                className={classes.title}
              >
                New Study Form
              </Typography>

              {/* AVAILABLE PROTOCOLS */}
              <div className={classes.subDiv}>
                <FormControl className={classes.protocolFormItem}>
                  <InputLabel>Select Protocol *</InputLabel>
                  <Select
                    className={classes.select}
                    onChange={handleFormChange({
                      field: "protocolId",
                      type: null,
                    })}
                    value={studyInfo.protocolId}
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    MenuProps={{ className: classNames(classes.menuScroll) }}
                  >
                    {props.protocolsListFetching === true ||
                    !props.localParticipantLogRecevied ? (
                      <MenuItem disabled={true} value="">
                        <CircularProgress size={25} className={classes.icon} />
                        Loading available protocols...
                      </MenuItem>
                    ) : (
                      Object.keys(props.protocols).map((protocol) => {
                        return (
                          <MenuItem
                            className={classes.menuItem}
                            key={props.protocols[protocol]["protocolId"]}
                            value={props.protocols[protocol]["protocolId"]}
                          >
                            {props.protocols[protocol]["protocolName"]}
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                </FormControl>
                {wifiEnabled && (
                  <div className={classes.cloudButtonWrap}>
                    <Tooltip
                      placement="top"
                      title="Sync Cloud Participant Log"
                      enterTouchDelay={300}
                      classes={{
                        tooltip: classes.tooltip,
                      }}
                    >
                      <Fab
                        className={classes.cloudButton}
                        disabled
                        onClick={() => props.fetchRemoteParticipantLog()}
                      >
                        {props.remoteParticipantLogRecevied ? (
                          <CloudDoneIcon />
                        ) : (
                          <CloudSync />
                        )}
                      </Fab>
                    </Tooltip>
                    {props.remoteParticipantLogFetching && (
                      <CircularProgress
                        size={45}
                        className={classes.fabProgress}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* PARTICIPANT TABS */}
              <div className={classes.tabContainer}>
                <Button
                  disabled={localState.testMode || studyInfo.protocolId === ""}
                  disableRipple
                  className={
                    localState.tab === "EXISTING_PARTICIPANT"
                      ? classes.tabSelected
                      : classes.tabUnselected
                  }
                  onClick={() => handleChangeTab("EXISTING_PARTICIPANT")}
                  onFocus={() => props.setVirtualKeyboard(false)}
                >
                  Existing Participant
                </Button>

                <Button
                  disableRipple
                  disabled={localState.testMode || studyInfo.protocolId === ""}
                  className={
                    localState.tab === "NEW_PARTICIPANT"
                      ? classes.tabSelected
                      : classes.tabUnselected
                  }
                  onClick={() => handleChangeTab("NEW_PARTICIPANT")}
                  onFocus={() => props.setVirtualKeyboard(false)}
                >
                  New Participant
                </Button>
              </div>

              {/* <AppBar
                color="default"
                className={classes.appBar}
                position="static"
              >
                <Tabs
                  // value={tab}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                  // onChange={handleTabChange}
                >
                  <Tab
                    label="Local Folder"
                    value="LOCAL"
                    // icon={<FolderIcon />}
                    // className={classes.tab}
                  />
                  <Tab
                    label="qwe Folder"
                    value="LOCAL"
                    // icon={<FolderIcon />}
                    // className={classes.tab}
                  />
                </Tabs>
              </AppBar> */}

              {/* PARTICIPANT ID */}
              <FormControl
                disabled={localState.testMode || studyInfo.protocolId === ""}
                className={classes.formTextItem}
                fullWidth
              >
                <FormLabel className={classes.formItemLabel}>
                  Participant ID*:
                </FormLabel>

                <TextField
                  className={classes.textFieldFormItem}
                  error={localState.participantIdSpecialCharacterCheck}
                  value={studyInfo.participantId}
                  disabled={
                    localState.testMode ||
                    studyInfo.protocolId === "" ||
                    localState.tab === "EXISTING_PARTICIPANT"
                  } // dont allow changing participant name if in test mode
                  InputProps={{
                    className: classes.inputBase,
                    startAdornment: !localState.testMode &&
                      studyInfo.protocolId !== "" &&
                      localState.tab === "NEW_PARTICIPANT" && (
                        <InputAdornment position="start">
                          {studyInfo.protocolId + "_"}
                        </InputAdornment>
                      ),
                  }}
                  onFocus={() => {
                    setKeyboardInput({
                      ...keyboardInput,
                      inputName: "participantId",
                    });
                    props.setVirtualKeyboard(true);
                  }}
                  onChange={handleFormChange({
                    field: "participantId",
                    type: null,
                  })}
                />
              </FormControl>
              <FormControl
                disabled={localState.testMode || studyInfo.protocolId === ""}
                className={classes.formTextItem}
                fullWidth
              >
                <FormLabel className={classes.formItemLabel}>
                  Secondary ID:
                </FormLabel>

                <TextField
                  className={classes.textFieldFormItem}
                  disabled={localState.testMode || studyInfo.protocolId === ""}
                  value={studyInfo.otherId}
                  onFocus={() => {
                    setKeyboardInput({
                      ...keyboardInput,
                      inputName: "otherId",
                    });
                    props.setVirtualKeyboard(true);
                  }}
                  onChange={handleFormChange({
                    field: "otherId",
                    type: null,
                  })}
                />
              </FormControl>

              {/* PARTICIPANT BIRTHYEAR */}
              <FormControl
                disabled={localState.testMode || studyInfo.protocolId === ""}
                className={classes.formTextItem}
              >
                <FormLabel className={classes.formItemLabel}>
                  Birth Year :
                </FormLabel>
                <WindowedSelect
                  maxMenuHeight={140}
                  placeholder={""}
                  options={birthYearWheel}
                  className={classes.textFieldFormItem}
                  onFocus={() => props.setVirtualKeyboard(false)}
                  components={{ DropdownIndicator }}
                  isDisabled={
                    localState.testMode || studyInfo.protocolId === ""
                  }
                  value={
                    studyInfo.birthYear === "" ||
                    studyInfo.birthYear === undefined ||
                    studyInfo.birthYear === null
                      ? null
                      : {
                          label: studyInfo.birthYear.toString(),
                          value: studyInfo.birthYear,
                        }
                  }
                  onChange={(item) =>
                    setStudyInfo({
                      ...studyInfo,
                      birthYear: item.value,
                    })
                  }
                  styles={{
                    placeholder: (provided, state) => ({
                      ...provided,
                      color: state.isDisabled ? colors.textDisabled : "#fff",
                      fontSize: "1rem",
                      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 400,
                      opacity: 1,
                      marginLeft: 0,
                      top: "70%",
                      left: 0,
                    }),
                    singleValue: (provided, state) => ({
                      ...provided,
                      color: state.isDisabled ? colors.textDisabled : "#fff",
                      fontSize: "1rem",
                      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 400,
                      opacity: 1,
                      marginLeft: 0,
                      top: "70%",
                      left: 0,
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      color: state.isSelected
                        ? colors[props.theme].primary
                        : "#fff",
                      backgroundColor: colors[props.theme].cardBkg,
                      fontSize: "0.875rem",
                      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.75,
                      ":active": {
                        backgroundColor: colors[props.theme].menuHoverColor,
                      },
                      ":hover": {
                        backgroundColor: colors[props.theme].menuHoverColor,
                      },
                      textAlign: "center",
                      height: 35, // must be type number
                      padding: "10px 10px 10px 10px",
                    }),
                    control: (provided, state) => {
                      return {
                        ...provided,
                        backgroundColor: colors[props.theme].cardBkg,
                        color: colors[props.theme].primary,
                        opacity: 1,
                        borderWidth: 0,
                        borderColor: "unset",
                        boxShadow: "none",
                        borderRadius: 0,
                        minHeight: "unset",
                        borderBottom: state.isDisabled
                          ? "1px dotted rgba(255, 255, 255, 0.7)"
                          : state.menuIsOpen
                          ? `1px solid ${colors[props.theme].primary}`
                          : "1px solid rgba(255, 255, 255, 0.7)",
                        "&:hover": {
                          borderWidth: 0,
                          borderColor: "unset",
                          borderBottom: state.isDisabled
                            ? "1px dotted rgba(255, 255, 255, 0.7)"
                            : state.menuIsOpen
                            ? `1px solid ${colors[props.theme].primary}`
                            : "1px solid rgba(255, 255, 255, 0.7)",
                        },
                      };
                    },
                    indicatorSeparator: () => ({
                      width: "0px",
                    }),

                    dropdownIndicator: (provided, state) => ({
                      ...provided,
                      opacity: 1,
                      paddingBottom: 0,
                      color: state.isDisabled ? colors.textDisabled : "#fff",
                      "&:hover": {
                        opacity: 1,
                        color: state.isDisabled ? colors.textDisabled : "#fff",
                      },
                    }),

                    menu: (provided, state) => {
                      return {
                        ...provided,
                        backgroundColor: colors[props.theme].cardBkg,
                        ...customTheme(props.theme).customScrollBar,
                      };
                    },
                    menuList: (provided) => ({
                      ...provided,
                      "::-webkit-scrollbar": {
                        width: "9px",
                      },
                      "::-webkit-scrollbar-thumb": {
                        backgroundColor:
                          props.theme === "light"
                            ? "rgba(0,0,0,.3)"
                            : "rgba(255,255,255,.2)",
                        outline: "1px solid grey",
                        borderRadius: "9px",
                      },
                    }),
                  }}
                />
              </FormControl>

              {/* SKIN TYPE*/}
              <FormControl
                disabled={localState.testMode || studyInfo.protocolId === ""}
                className={classes.formGroupItem}
                fullWidth
              >
                <FormLabel className={classes.formItemLabel}>
                  Skin Type:
                </FormLabel>

                <ButtonGroup
                  disabled={localState.testMode || studyInfo.protocolId === ""}
                  className={classes.skinTypeFormButtonGroup}
                >
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.skinTypeFormButton}
                    style={{
                      backgroundColor: colors.skinType1,
                      color:
                        localState.testMode || studyInfo.protocolId === ""
                          ? colors.skinTypeDisabled
                          : colors.skinTypeText,
                      border:
                        studyInfo.skinType === 1
                          ? `3px solid ${colors[props.theme].primary}`
                          : `3px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({ field: "skinType", type: 1 })}
                  >
                    Type I{/* : light, pale white */}
                  </Button>
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.skinTypeFormButton}
                    style={{
                      backgroundColor: colors.skinType2,
                      color:
                        localState.testMode || studyInfo.protocolId === ""
                          ? colors.skinTypeDisabled
                          : colors.skinTypeText,
                      border:
                        studyInfo.skinType === 2
                          ? `3px solid ${colors[props.theme].primary}`
                          : `3px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({ field: "skinType", type: 2 })}
                  >
                    Type II
                    {/* : white, fair */}
                  </Button>
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.skinTypeFormButton}
                    style={{
                      backgroundColor: colors.skinType3,
                      color:
                        localState.testMode || studyInfo.protocolId === ""
                          ? colors.skinTypeDisabled
                          : colors.skinTypeText,
                      border:
                        studyInfo.skinType === 3
                          ? `3px solid ${colors[props.theme].primary}`
                          : `3px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({ field: "skinType", type: 3 })}
                  >
                    Type III
                    {/* : medium, white to olive */}
                  </Button>
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.skinTypeFormButton}
                    style={{
                      backgroundColor: colors.skinType4,
                      border:
                        studyInfo.skinType === 4
                          ? `3px solid ${colors[props.theme].primary}`
                          : `3px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({ field: "skinType", type: 4 })}
                  >
                    Type IV
                    {/* : olive, moderate brown */}
                  </Button>
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.skinTypeFormButton}
                    style={{
                      backgroundColor: colors.skinType5,
                      border:
                        studyInfo.skinType === 5
                          ? `3px solid ${colors[props.theme].primary}`
                          : `3px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({ field: "skinType", type: 5 })}
                  >
                    Type V{/* : brown, dark brown */}
                  </Button>
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.skinTypeFormButton}
                    style={{
                      backgroundColor: colors.skinType6,
                      border:
                        studyInfo.skinType === 6
                          ? `3px solid ${colors[props.theme].primary}`
                          : `3px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({ field: "skinType", type: 6 })}
                  >
                    Type VI
                    {/* : black, very dark brown to black */}
                  </Button>
                </ButtonGroup>
              </FormControl>
              {/* GENDER */}
              <FormControl
                disabled={localState.testMode || studyInfo.protocolId === ""}
                className={classes.formGroupItem}
                fullWidth
              >
                <FormLabel className={classes.formItemLabel}>Gender:</FormLabel>

                <ButtonGroup
                  disabled={localState.testMode || studyInfo.protocolId === ""}
                  className={classes.genderFormButtonGroup}
                >
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.genderFormButton}
                    style={{
                      borderRadius: "4px",
                      border:
                        studyInfo.gender === "female"
                          ? `2px solid ${colors[props.theme].primary}`
                          : `2px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({
                      field: "gender",
                      type: "female",
                    })}
                  >
                    Female
                  </Button>
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.genderFormButton}
                    style={{
                      borderRadius: "4px",
                      border:
                        studyInfo.gender === "male"
                          ? `2px solid ${colors[props.theme].primary}`
                          : `2px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({
                      field: "gender",
                      type: "male",
                    })}
                  >
                    Male
                  </Button>
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.genderFormButton}
                    style={{
                      borderRadius: "4px",
                      border:
                        studyInfo.gender === "other"
                          ? `2px solid ${colors[props.theme].primary}`
                          : `2px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({
                      field: "gender",
                      type: "other",
                    })}
                  >
                    Gender Variant
                  </Button>
                  <Button
                    onFocus={() => {
                      props.setVirtualKeyboard(false);
                    }}
                    className={classes.genderFormButton}
                    style={{
                      borderRadius: "4px",
                      border:
                        studyInfo.gender === "none"
                          ? `2px solid ${colors[props.theme].primary}`
                          : `2px solid ${colors[props.theme].menuHoverColor}`,
                    }}
                    onClick={handleFormChange({
                      field: "gender",
                      type: "none",
                    })}
                  >
                    Prefer not to answer
                  </Button>
                </ButtonGroup>
              </FormControl>
              <div className={classes.testModeDiv}>
                {localState.tab === "NEW_PARTICIPANT" && (
                  <FormControlLabel
                    className={classes.testModeFormControl}
                    disabled={studyInfo.protocolId === ""}
                    control={
                      <Checkbox
                        color="primary"
                        className={classes.checkbox}
                        onChange={(e) => handleTestMode(e)}
                        checked={localState.testMode}
                        value={localState.testMode}
                      />
                    }
                    label="Test mode"
                  />
                )}
              </div>
              {/* BUTTON */}
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSubmitRequest()}
                  className={classes.button}
                  disabled={
                    //Disasble if the information is not entered
                    studyInfo.protocolId.length === 0 ||
                    studyInfo.participantId.length === 0 ||
                    localState.participantIdSpecialCharacterCheck
                  }
                >
                  START STUDY
                </Button>
              </div>
            </Card>
          </Grid>
        </Grid>

        {/* COPYRIGHT INFO */}
        <Grid item align="center" className={classes.copyright}>
          <Typography variant="caption" gutterBottom>
            This software is for investigational research use only. It is
            provided as-is with no warranty. &copy;{new Date().getFullYear()}{" "}
            MicroElastic Ultrasound Systems
          </Typography>
        </Grid>
      </Grid>

      {/* SNACKBAR FOR MESSAGE DISPLAY */}
      <SnackbarInfo />
      <VirtualKeyboard
        anchor={"center"}
        numberPad={false}
        keyboardInitialInput={{
          participantId: studyInfo.participantId,
        }}
        keyboardInputUpdate={keyboardInput.inputUpdate}
        keyboardInputName={keyboardInput.inputName}
        keyboardChange={handleFormChange({
          field: keyboardInput.inputName,
          type: null,
        })}
      />

      {props.existingParticipantsDialog && (
        <ExistingParticipantDialog
          open={props.existingParticipantsDialog}
          selectedProtocolId={studyInfo.protocolId}
          selectedProtocolName={studyInfo.protocolName}
          handleClickExistingParticipant={(participantId) =>
            handleClickExistingParticipant(participantId)
          }
          onClose={() => {
            setLocalState({ ...localState, tab: "NEW_PARTICIPANT" });
            props.toggleExistingParticipantsDialog();
          }}
        />
      )}

      {localState.warningDialog.status && (
        <ParticipantWarningDialog
          open={localState.warningDialog.status}
          theme={props.theme}
          wifiConnection={props.wifiConnection}
          fetchCheckWifi={() => props.fetchCheckWifi(false, false)}
          prompt={localState.warningDialog.prompt}
          remoteParticipantLogRecevied={props.remoteParticipantLogRecevied}
          resolveParticipantId={(prompt, updateRemote) =>
            handleParticipantWarningDialog(prompt, updateRemote)
          }
        />
      )}
    </Grid>
  );
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      createNewStudy,
      setVirtualKeyboard,
      toggleExistingParticipantsDialog,
      fetchEditExistingParticipantInfo,
      setView,
      setTemplatePrompt,
      fetchLocalStudiesList,
      fetchRemoteParticipantLog,
      fetchCheckWifi,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    protocols: state.availableProtocols.protocolsById,
    protocolsListFetching: state.availableProtocols.protocolsListFetching,

    logParticipantsById: state.availableParticipants.logParticipantsById,
    logParticipantAllIds: state.availableParticipants.logParticipantAllIds,
    logParticipantsByProtocolId:
      state.availableParticipants.logParticipantsByProtocolId,

    // localParticipantLogFetching: state.availableParticipants.localParticipantLogFetching,
    localParticipantLogRecevied:
      state.availableParticipants.localParticipantLogRecevied,
    remoteParticipantLogFetching:
      state.availableParticipants.remoteParticipantLogFetching,
    remoteParticipantLogRecevied:
      state.availableParticipants.remoteParticipantLogRecevied,

    wifiConnection: state.utility.wifi.wifiConnection,

    existingParticipantsDialog: state.utility.dialog.existingParticipantsDialog,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(NewStudy);
